import React, { Context, createContext, useReducer, useEffect } from 'react';
import { User, Merchant } from '@stax-core/types-omni';
import {
  AuthContextProps,
  AuthContext,
  AuthAction,
  AuthState,
  AuthActionType,
  AuthDatum,
  AuthEffectType,
  Auth,
} from './Auth.types';

const AuthStore = createContext({} as AuthContext) as Context<AuthContext>;

const getInitialState = (): AuthState => {
  try {
    const savedAuth = JSON.parse(localStorage.getItem('selfreg.authdata')!);
    const savedIndex = +localStorage.getItem('selfreg.authindex')!;

    if (savedAuth && typeof savedAuth === 'object') {
      const auth = savedAuth as Auth;
      return {
        auth: auth,
        index: savedIndex || 0,
        authToken: auth.token,
        status: 'authed.stale',
        effect: null,
      } as AuthState;
    } else {
      throw new Error('unable to hydrate AuthStore from localStorage');
    }
  } catch (error) {
    return {
      auth: null,
      authToken: null,
      status: 'notauthed',
      effect: null,
    };
  }
};

const initialState = getInitialState();

type AuthReducer = (state: AuthState, action: AuthAction) => AuthState;

const authReducer: AuthReducer = (state, action) => {
  switch (action.type) {
    case AuthActionType.UPDATE_AUTH: {
      const data = action.payload as AuthDatum[];
      const index = 0;
      const { merchant, user, token } = data[index] ?? {};
      const brand = merchant?.brand || user?.brand;

      return {
        ...state,
        status: 'authed.fresh',
        authToken: token,
        auth: { data, index, merchant, user, token, brand },
      };
    }
    case AuthActionType.UPDATE_AUTH_USER: {
      const user = action.payload as User;
      return {
        ...state,
        auth: { ...state.auth!, user },
      };
    }
    case AuthActionType.UPDATE_AUTH_MERCHANT: {
      const merchant = action.payload as Merchant;
      return {
        ...state,
        auth: { ...state.auth!, merchant },
      };
    }
    case AuthActionType.REMOVE_AUTH: {
      return { ...state, auth: null };
    }
    case AuthActionType.UPDATE_AUTH_INDEX: {
      const data = state.auth!.data;
      const index = action.payload as number;
      const { merchant, user, token } = data[index];
      const brand = merchant?.brand || user?.brand;

      return {
        ...state,
        auth: { data, index, merchant, user, token, brand },
      };
    }
    default:
      return state;
  }
};

const effectReducer: AuthReducer = (state, action) => {
  switch (action.type) {
    case AuthActionType.UPDATE_AUTH:
    case AuthActionType.UPDATE_AUTH_USER:
    case AuthActionType.UPDATE_AUTH_MERCHANT:
      return {
        ...state,
        effect: {
          type: AuthEffectType.UPDATE_AUTH,
          payload: state.auth,
        },
      };
    case AuthActionType.UPDATE_AUTH_INDEX:
      return {
        ...state,
        effect: {
          type: AuthEffectType.UPDATE_AUTH_INDEX,
          payload: state.auth,
        },
      };
    case AuthActionType.REMOVE_AUTH:
      return {
        ...state,
        effect: {
          type: AuthEffectType.REMOVE_AUTH,
        },
      };
    default: {
      return { ...state };
    }
  }
};

const AuthProvider = (props: AuthContextProps) => {
  const [state, dispatch] = useReducer(
    (state, action) => effectReducer(authReducer(state, action), action),
    props.initialState ?? initialState,
  );

  const { effect } = state;

  useEffect(() => {
    const effectType = effect && effect.type;
    switch (effectType) {
      case AuthEffectType.UPDATE_AUTH:
        localStorage.setItem(
          'selfreg.authdata',
          JSON.stringify(effect!.payload),
        );

        break;
      case AuthEffectType.REMOVE_AUTH:
        localStorage.removeItem('selfreg.authindex');
        localStorage.removeItem('selfreg.authdata');
        break;
      case AuthEffectType.UPDATE_AUTH_INDEX:
        localStorage.setItem('selfreg.authindex', effect!.payload!.index as any);
        localStorage.setItem(
          'selfreg.authdata',
          JSON.stringify(effect!.payload),
        );

        break;
      default:
        break;
    }
  }, [effect]);

  const localContext: AuthContext = {
    state,
    dispatch,
  };

  return (
    <AuthStore.Provider value={localContext}>
      {props.children}
    </AuthStore.Provider>
  );
};

export { AuthStore, AuthProvider };
